import { API_URL, USE_REMOTE_DEV_BACKEND } from '../../constants';
import { TAuthUser } from '../../../spa/store/general/types';
import { getDispatch } from '../../../spa/store';
import { postRequest, postRequestWithoutToken } from '../../utils/requestService';

export interface IUserSettings {
  pause: number,
  hoverAutoPause: boolean,
  autoSync: boolean,
  showNativeCaptions: boolean,
  phraseListPauseDelay: number,
  phraseListStartPlayOffset: number,
  phraseListEndPlayOffset: number,
  showOnboarding: string,
  pauseOnNote: boolean,
  phraseListSaveType: number,
  showTranslations: boolean,
  autoSpeechRate: number,
}

export interface IAppSettings {
  saveUserTranslations: boolean;
  statServersUrl: string;
}

export interface IUserRestResponse {
  authUser: TAuthUser,
  settings: IUserSettings,
  appSettings: IAppSettings
}

export class UserRest {

  private static AUTH_USER = API_URL + '/user/authCode';
  private static USER_BY_EMAIL = API_URL + '/user/authCodeByEmail';
  private static USER_BY_SESSION_URL = API_URL + '/user/info';
  private static SYNC_AUTH_SERVICE_SESSION_URL = API_URL + '/user/syncAuthServiceSession';
  private static USER_SETTINGS_SAVE = API_URL + '/users/settingsSave';
  private static USER_ROLES_SAVE = API_URL + '/users/rolesSave';
  private static USER_UPDATE = API_URL + '/users/update';
  private static USER_SEND_CONFIRM_EMAIL = API_URL + '/users/sendUserConfirmEmail';
  private static USER_CREATE_ANONYMOUS = API_URL + '/user/createAnonymous';
  private static USER_BY_HASH = API_URL + '/user/authByHash';
  private static USER_LOGIN_ADMIN = API_URL + '/users/loginAdmin';
  private static USER_LOGOUT = API_URL + '/user/logout';

  public static async getAuthUserByCode(code: string, callback: boolean): Promise<IUserRestResponse | null> {
    const devmode = USE_REMOTE_DEV_BACKEND ? '1' : '';
    const headers: any = {
      code,
      devmode
    };
    if (callback) {
      headers.callback = '1';
    }
    try {
      const response = await fetch(UserRest.AUTH_USER, { headers, credentials: 'include' });
      const result = await response.json();
      return result;
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  // Same call without an Authorization header — for bootstrap, before any user is in the store.
  // The route only checks the admin's express-session, so the header isn't needed anyway.
  public static async getUserByEmailWithoutToken(email: string): Promise<IUserRestResponse | null> {
    try {
      return await postRequestWithoutToken(UserRest.USER_BY_EMAIL, { email });
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  public static async getUserByEmail(email: string): Promise<IUserRestResponse | null> {
    try {
      const dispatch = getDispatch();
      return await dispatch(postRequest(UserRest.USER_BY_EMAIL, { email }));
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  // Auth bootstrap runs before anything renders; without a timeout a hanging /user/info
  // (e.g. no session cookie) leaves every route stuck on a loader. Fail fast to null instead.
  private static USER_BY_SESSION_TIMEOUT = 10000;

  // Whoever this backend already considers signed in on this browser: the lm_sess_user cookie
  // first, the legacy Google-token cookies as a fallback (see backend userAuthInfoAction.ts). No
  // session there answers 404, which lands in the catch as null.
  public static async getUserBySession(): Promise<IUserRestResponse | null> {
    try {
      return await postRequestWithoutToken(UserRest.USER_BY_SESSION_URL, {}, { withCredentials: true, timeout: UserRest.USER_BY_SESSION_TIMEOUT });
    } catch (e) {
      return null;
    }
  }

  // Hands the backend the temporary code a direct browser fetch to AUTH_SERVICE_URL/user-code came
  // back with (see AuthManager.autoLogin) so it can exchange it server-side
  // (AUTH_SERVICE_URL/user, POST {code}) and mint/replace this app's own session for whoever that
  // proves is signed in — no login-page redirect needed. `null` covers both "no code to sync"
  // (nobody signed in there, or the browser withheld the auth service's cookie) and "the backend
  // couldn't exchange it" (bad/expired code, auth service down): either way there's nothing to
  // apply, and the caller falls back to whatever local session it already has.
  public static async syncAuthServiceSession(code: string): Promise<IUserRestResponse | null> {
    try {
      return await postRequestWithoutToken(UserRest.SYNC_AUTH_SERVICE_SESSION_URL, { code }, { withCredentials: true, timeout: UserRest.USER_BY_SESSION_TIMEOUT });
    } catch (e) {
      return null;
    }
  }

  public static async saveSettings(settings: Partial<IUserSettings>) {
    const dispatch = getDispatch();
    return await dispatch(postRequest(UserRest.USER_SETTINGS_SAVE, settings));
  }

  public static async saveRoles(roles: number[]): Promise<number[]> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(UserRest.USER_ROLES_SAVE, roles));
  }

  public static async updateUser(data: Partial<TAuthUser>): Promise<number[]> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(UserRest.USER_UPDATE, data));
  }

  public static async sendConfirmEmail(): Promise<Partial<TAuthUser>> {
    const dispatch = getDispatch();
    return await dispatch(postRequest(UserRest.USER_SEND_CONFIRM_EMAIL, {}));
  }

  public static async createAnonymousUserAction(targetLangCode: string, nativeLangCode: string): Promise<IUserRestResponse> {
    return await postRequestWithoutToken(UserRest.USER_CREATE_ANONYMOUS, { targetLangCode, nativeLangCode });
  }

  public static async getUserByHash(hash: string): Promise<IUserRestResponse | null> {
    try {
      return await postRequestWithoutToken(UserRest.USER_BY_HASH, { hash });
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  public static async loginAdmin() {
    const dispatch = getDispatch();
    return await dispatch(postRequest(UserRest.USER_LOGIN_ADMIN, {}));
  }

  public static async logout() {
    const dispatch = getDispatch();
    return await dispatch(postRequest(UserRest.USER_LOGOUT, {}));
  }

}
